import { graphql, useStaticQuery } from "gatsby"
import { useState, useMemo } from "react"
import FilterBar from "components/FilterBar"
import NoEvents from "components/NoEvents"
import { SEOHead } from "components/SEO"
import useEvents from "effects/useEvents"
import Section from "components/Layout/Section"
import SimpleHeader from "components/Layout/SimpleHeader"
import * as css from "components/pages/events/Events.module.css"
import PastEventsSection from "components/pages/events/PastEventsSection"
import UpcomingEventsSection from "components/pages/events/UpcomingEventsSection"
import Layout from "components/ui/Layout"

const EVENT_FILTERS = [
  { id: null, label: "All" },
  { id: "webinar", label: "Live Demos" },
  { id: "conference", label: "Conferences" },
]

const TIME_FILTERS = [
  { id: null, label: "All" },
  { id: "upcoming", label: "Upcoming" },
  { id: "past", label: "Past" },
]

/**
 * Format ISO date string to display format
 */
function formatEventDate(isoString, endIsoString = null, allDay = false) {
  if (!isoString) return null

  if (allDay) {
    // Parse date parts directly to avoid UTC offset shifting the day
    const [sy, sm, sd] = isoString.slice(0, 10).split("-").map(Number)
    if (endIsoString) {
      const [ey, em, ed] = endIsoString.slice(0, 10).split("-").map(Number)
      if (sm === em && sy === ey) {
        const month = new Date(sy, sm - 1, sd).toLocaleDateString("en-US", {
          month: "long",
        })
        return `${month} ${sd}–${ed}, ${sy}`
      }
      const startStr = new Date(sy, sm - 1, sd).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      })
      const endStr = new Date(ey, em - 1, ed).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      })
      return `${startStr} – ${endStr}`
    }
    return new Date(sy, sm - 1, sd).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    })
  }

  return new Date(isoString).toLocaleDateString("en-US", {
    month: "long",
    day: "2-digit",
    year: "numeric",
    timeZone: "America/Los_Angeles",
  })
}

/**
 * Transform local YAML events to match Contentful structure for useEvents hook
 */
function transformEvents(nodes) {
  return {
    edges: nodes
      .filter((node) => node.category !== "Preset Training Session")
      .map((node) => ({
        node: {
          ...node,
          date: formatEventDate(node.date, node.endDate, node.allDay),
          // Keep raw dates for filtering in useEvents
          rawDate: node.date,
          rawEndDate: node.endDate,
          // Transform description to match expected structure
          description: node.description
            ? { childMarkdownRemark: { html: node.description } }
            : null,
        },
      })),
  }
}

function EventsPage({ location: { pathname } }) {
  const [activeTypeFilter, setActiveTypeFilter] = useState(null)
  const [activeTimeFilter, setActiveTimeFilter] = useState(null)

  const data = useStaticQuery(graphql`
    {
      allEventsYaml(sort: { date: ASC }) {
        nodes {
          id
          title
          slug
          type
          category
          date
          endDate
          allDay
          role
          duration
          location
          videoUrl
          description
          thumbnail {
            url
            alt
          }
          buttons {
            label
            url
          }
        }
      }
    }
  `)

  const allEvents = transformEvents(data.allEventsYaml.nodes)
  const { upcomingEvents: allUpcoming, pastEvents: allPast } = useEvents(
    allEvents,
    null
  )

  const filterByType = (list) => {
    if (!activeTypeFilter) return list
    return list.filter((e) =>
      activeTypeFilter === "conference"
        ? e.type === "conference"
        : e.type !== "conference"
    )
  }

  const upcomingEvents = useMemo(
    () => filterByType(allUpcoming),
    [allUpcoming, activeTypeFilter]
  )
  const pastEvents = useMemo(
    () => filterByType(allPast),
    [allPast, activeTypeFilter]
  )

  return (
    <Layout
      headerFloating
      trackingName="Events"
      pageContentType="listing-page"
      pageName="events"
      pagePath={pathname}
    >
      <Section className={css.hero}>
        <SimpleHeader
          level="h1"
          label="Live demos, meetups, and conferences"
          title="Events"
          titleSize="xxl"
          dash="horizontal"
        />
      </Section>
      {/* <Section className={css.meetupButton}>
        <Overflow>
          <MeetupButton />
        </Overflow>
      </Section> */}
      <Section className={css.eventsListingSection}>
        <div className={css.eventsToolbar}>
          <a
            href={`webcal://${typeof window !== "undefined" ? window.location.host : "preset.io"}/events.ics`}
            className={css.subscribeButton}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            Subscribe to calendar
          </a>
          <div className={css.toolbarSeparator} />
          <span className={css.toolbarLabel}>Time:</span>
          <FilterBar
            filters={TIME_FILTERS}
            activeFilter={activeTimeFilter}
            onChange={setActiveTimeFilter}
            className={css.toolbarFilter}
          />
          <div className={css.toolbarSeparator} />
          <span className={css.toolbarLabel}>Event type:</span>
          <FilterBar
            filters={EVENT_FILTERS}
            activeFilter={activeTypeFilter}
            onChange={setActiveTypeFilter}
            className={css.toolbarFilter}
          />
        </div>
        {activeTimeFilter !== "past" &&
          (upcomingEvents.length > 0 ? (
            <UpcomingEventsSection events={upcomingEvents} />
          ) : (
            <NoEvents
              className={css.noEvents}
              message="There are no upcoming events scheduled. You can watch recordings of past events below."
            />
          ))}
        {activeTimeFilter !== "upcoming" && pastEvents.length > 0 && (
          <PastEventsSection events={pastEvents.reverse()} />
        )}
      </Section>
    </Layout>
  )
}

// Export a Head component for Gatsby Head API
export function Head() {
  return (
    <SEOHead
      title="Attend a Preset Event - Live Demos, Meetups & Conferences"
      description="Register for upcoming Preset events — live demos, community meetups, and conference talks. Watch recordings of previous events and connect with the Preset team."
    />
  )
}

export default EventsPage
